<template>
  <div class="question-screen">
    <ProgressHeader :current="current" :total="total" tone="orange" @close="$emit('close')" />
    <CircularTimer :remaining="remaining" :progress="progress" />

    <section class="question-card question-card--cyan">
      <ProblemVisual
        :visual-type="question.visualType"
        :image-src="question.imageSrc"
        :image-alt="question.imageAlt"
      />
      <h2 class="question-card__prompt">{{ question.prompt }}</h2>
    </section>

    <div class="choice-list">
      <ChoiceButton
        v-for="choice in question.choices"
        :key="choice.id"
        :choice="choice"
        :selected="selectedChoiceId === choice.id"
        :disabled="locked"
        @select="select(choice.id, true)"
      />
    </div>

    <div v-if="feedbackText" class="feedback-overlay" :class="feedbackTone">
      <strong>{{ feedbackText }}</strong>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import CircularTimer from '@/components/CircularTimer.vue'
import ChoiceButton from '@/components/ChoiceButton.vue'
import ProblemVisual from '@/components/ProblemVisual.vue'
import ProgressHeader from '@/components/ProgressHeader.vue'
import { useCountdown } from '@/composables/useCountdown'
import type { Question } from '@/types/content'

const props = defineProps<{
  question: Question
  current: number
  total: number
}>()

const emit = defineEmits<{
  answered: [payload: { choiceId: string; selectedByUser: boolean; elapsedMs: number }]
  close: []
}>()

const selectedChoiceId = ref('')
const locked = ref(false)
const feedbackText = ref('')
const active = ref(true)
const duration = toRef(props.question, 'timerSeconds')

const { remaining, progress, stop, reset, elapsedMs } = useCountdown(duration, active, handleTimeout)

const feedbackTone = computed(() => {
  if (!feedbackText.value) return ''
  return feedbackText.value.includes('상위') ? 'feedback-overlay--up' : 'feedback-overlay--down'
})

function timeoutChoiceId(): string {
  if (props.question.timeoutChoiceId === '__last__') {
    return props.question.choices.at(-1)?.id ?? ''
  }
  return props.question.timeoutChoiceId
}

function handleTimeout(): void {
  select(timeoutChoiceId(), false)
}

function select(choiceId: string, selectedByUser: boolean): void {
  if (locked.value || !choiceId) return
  locked.value = true
  active.value = false
  stop()
  selectedChoiceId.value = choiceId
  const elapsed = selectedByUser ? elapsedMs() : props.question.timerSeconds * 1000

  if (props.question.feedback) {
    const isCorrect = choiceId === props.question.correctChoiceId
    feedbackText.value = isCorrect
      ? props.question.feedback.correctText ?? ''
      : props.question.feedback.incorrectText ?? ''
  }

  window.setTimeout(() => {
    emit('answered', { choiceId, selectedByUser, elapsedMs: elapsed })
  }, feedbackText.value ? 900 : 450)
}

watch(
  () => props.question.id,
  () => {
    selectedChoiceId.value = ''
    locked.value = false
    feedbackText.value = ''
    active.value = true
    reset()
  },
)
</script>
